import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface OrderData {
    id: string;
    userId: string; // Keep for display/logic, but populated from UserRef or userId
    userDisplayName: string;
    shikakuId: string;
    shikakuName: string;
    orderNumber: string;
    createdAt: Timestamp;
    expiresAt: Timestamp;
    status: string;
    isExamFinished?: boolean;
}

interface UserOption {
    id: string;
    display_name: string;
}

interface ShikakuOption {
    id: string;
    ShikakuName: string;
}

export default function OrderManagement() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<OrderData[]>([]);
    const [users, setUsers] = useState<UserOption[]>([]);
    const [shikakuList, setShikakuList] = useState<ShikakuOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAddingOrder, setIsAddingOrder] = useState(false);
    const [newOrderForm, setNewOrderForm] = useState({
        userId: '',
        shikakuId: '',
        status: 'active',
        isExamFinished: false
    });
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{
        created: number;
        updated: number;
        errors: number;
        errorDetails: string[];
    } | null>(null);

    useEffect(() => {
        fetchOrders();
        fetchUsers();
        fetchShikaku();
    }, []);

    const fetchOrders = async () => {
        try {
            const ordersSnapshot = await getDocs(collection(db, 'Order'));
            console.log('Total orders found:', ordersSnapshot.docs.length);

            const ordersData = await Promise.all(
                ordersSnapshot.docs.map(async (orderDoc) => {
                    try {
                        const orderData = orderDoc.data();
                        console.log('Processing order:', orderDoc.id, orderData);

                        // User情報を取得 (UserRef優先、なければuserId)
                        let userDisplayName = '-';
                        let userIdStr = '-';
                        const userRef = orderData.UserRef || orderData.userId;

                        if (userRef) {
                            try {
                                // userRefが参照型か確認
                                const userDocRef = userRef.path ? userRef : doc(db, 'User', userRef);
                                const userDoc = await getDoc(userDocRef);
                                if (userDoc.exists()) {
                                    userDisplayName = (userDoc.data() as any)?.display_name || '-';
                                    userIdStr = userDoc.id;
                                }
                            } catch (error) {
                                console.error('Error fetching user for order', orderDoc.id, ':', error);
                            }
                        }

                        // Shikaku情報を取得
                        let shikakuName = '-';
                        if (orderData.shikakuId) {
                            try {
                                const shikakuDoc = await getDoc(orderData.shikakuId);
                                shikakuName = shikakuDoc.exists() ? ((shikakuDoc.data() as any)?.ShikakuName || '-') : '-';
                            } catch (error) {
                                console.error('Error fetching shikaku for order', orderDoc.id, ':', error);
                            }
                        }

                        return {
                            id: orderDoc.id,
                            userId: userIdStr,
                            userDisplayName,
                            shikakuId: orderData.shikakuId?.id || '-',
                            shikakuName,
                            orderNumber: orderData.orderNumber || '-',
                            createdAt: orderData.createdAt,
                            expiresAt: orderData.expiresAt,
                            status: orderData.status || 'active',
                            isExamFinished: orderData.isExamFinished || false
                        };
                    } catch (error) {
                        console.error('Error processing order:', orderDoc.id, error);
                        return null;
                    }
                })
            );

            // Filter out null values
            const validOrders = ordersData.filter(order => order !== null) as OrderData[];
            console.log('Valid orders:', validOrders.length, validOrders);
            setOrders(validOrders);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching orders:', error);
            alert(`Order一覧の取得に失敗しました: ${error}`);
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'User'));
            const usersData = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                display_name: doc.data().display_name || '-'
            }));
            setUsers(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchShikaku = async () => {
        try {
            const shikakuSnapshot = await getDocs(collection(db, 'Shikaku'));
            const shikakuData = shikakuSnapshot.docs.map(doc => ({
                id: doc.id,
                ShikakuName: doc.data().ShikakuName || '-'
            }));
            setShikakuList(shikakuData);
        } catch (error) {
            console.error('Error fetching shikaku:', error);
        }
    };

    const generateOrderNumber = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `ORD-${year}${month}${day}-${random}`;
    };

    const handleAddOrder = async () => {
        if (!newOrderForm.userId || !newOrderForm.shikakuId) {
            alert('ユーザーと資格を選択してください');
            return;
        }

        try {
            const createdAt = Timestamp.now();
            const expiresAt = Timestamp.fromDate(
                new Date(createdAt.toDate().getTime() + 2 * 365 * 24 * 60 * 60 * 1000) // 2年後
            );

            await addDoc(collection(db, 'Order'), {
                UserRef: doc(db, 'User', newOrderForm.userId),
                shikakuId: doc(db, 'Shikaku', newOrderForm.shikakuId),
                orderNumber: generateOrderNumber(),
                createdAt,
                expiresAt,
                status: newOrderForm.status,
                isExamFinished: newOrderForm.isExamFinished
            });

            alert('Orderを追加しました');
            setIsAddingOrder(false);
            setNewOrderForm({ userId: '', shikakuId: '', status: 'active', isExamFinished: false });
            fetchOrders();
        } catch (error) {
            console.error('Error adding order:', error);
            alert(`追加に失敗しました: ${error}`);
        }
    };

    const handleUpdateStatus = async (orderId: string, newStatus: string) => {
        try {
            await updateDoc(doc(db, 'Order', orderId), { status: newStatus });
            alert('ステータスを更新しました');
            fetchOrders();
        } catch (error) {
            console.error('Error updating status:', error);
            alert('更新に失敗しました');
        }
    };

    const handleUpdateExamFinished = async (orderId: string, isFinished: boolean) => {
        try {
            await updateDoc(doc(db, 'Order', orderId), { isExamFinished: isFinished });
            fetchOrders();
        } catch (error) {
            console.error('Error updating exam finished status:', error);
            alert('更新に失敗しました');
        }
    };

    const handleDelete = async (orderId: string) => {
        if (!confirm('このOrderを削除してもよろしいですか？')) return;

        try {
            await deleteDoc(doc(db, 'Order', orderId));
            alert('Orderを削除しました');
            fetchOrders();
        } catch (error) {
            console.error('Error deleting order:', error);
            alert('削除に失敗しました');
        }
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleSyncFromSpreadsheet = async () => {
        if (!confirm('スプレッドシートからOrderデータを同期しますか?')) return;

        setIsSyncing(true);
        try {
            const functions = getFunctions(undefined, 'asia-northeast1');
            const syncFunction = httpsCallable(functions, 'syncOrdersFromSheet');
            const result = await syncFunction();

            const data = result.data as any;
            if (data.success) {
                setSyncResult(data.results);
                alert(`同期完了:\n作成: ${data.results.created}件\n更新: ${data.results.updated}件\nエラー: ${data.results.errors}件`);
                await fetchOrders(); // Reload orders
            } else {
                alert('同期に失敗しました');
            }
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期エラー: ${error.message || '不明なエラー'}`);
        } finally {
            setIsSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">読み込み中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Order管理</h1>
                        <p className="text-gray-600">ユーザーと資格の紐付け管理</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSyncFromSpreadsheet}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                        >
                            {isSyncing ? '同期中...' : 'スプレッドシートから同期'}
                        </button>
                        <button
                            onClick={() => setIsAddingOrder(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            ＋ Order追加
                        </button>
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            ダッシュボードに戻る
                        </button>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            ログアウト
                        </button>
                    </div>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    合計: {orders.length} Order
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-100 border-b">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">注文番号</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ユーザー</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">資格</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">申込日</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">有効期限</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ステータス</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">受験済</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {orders.map((order) => (
                                    <tr key={order.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-800 font-mono">{order.orderNumber}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{order.userDisplayName}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{order.shikakuName}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {order.createdAt?.toDate().toLocaleDateString('ja-JP')}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {order.expiresAt?.toDate().toLocaleDateString('ja-JP')}
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={order.status}
                                                onChange={(e) => handleUpdateStatus(order.id, e.target.value)}
                                                className={`px-2 py-1 text-xs rounded-full ${order.status === 'active'
                                                    ? 'bg-green-100 text-green-700'
                                                    : order.status === 'completed'
                                                        ? 'bg-blue-100 text-blue-700'
                                                        : 'bg-gray-100 text-gray-700'
                                                    }`}
                                            >
                                                <option value="active">有効</option>
                                                <option value="completed">完了</option>
                                                <option value="cancelled">キャンセル</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                checked={order.isExamFinished || false}
                                                onChange={(e) => handleUpdateExamFinished(order.id, e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleDelete(order.id)}
                                                className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                            >
                                                削除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {orders.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            Orderが登録されていません
                        </div>
                    )}
                </div>
            </div>

            {/* Add Order Modal */}
            {isAddingOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">Order追加</h2>
                            <p className="text-sm text-gray-600 mt-1">ユーザーと資格を紐付けます</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ユーザー <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newOrderForm.userId}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, userId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">選択してください</option>
                                    {users.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.display_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    資格 <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newOrderForm.shikakuId}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, shikakuId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">選択してください</option>
                                    {shikakuList.map((shikaku) => (
                                        <option key={shikaku.id} value={shikaku.id}>
                                            {shikaku.ShikakuName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
                                <select
                                    value={newOrderForm.status}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, status: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="active">有効</option>
                                    <option value="completed">完了</option>
                                    <option value="cancelled">キャンセル</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isExamFinished"
                                    checked={newOrderForm.isExamFinished}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, isExamFinished: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isExamFinished" className="text-sm font-medium text-gray-700">
                                    受験済みとして登録
                                </label>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <p className="text-sm text-blue-700">
                                    ※ 注文番号は自動生成されます<br />
                                    ※ 申込日は現在日時、有効期限は2年後に自動設定されます
                                </p>
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setIsAddingOrder(false);
                                    setNewOrderForm({ userId: '', shikakuId: '', status: 'active', isExamFinished: false });
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleAddOrder}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                追加
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sync Result Modal */}
            {syncResult && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">同期結果</h2>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
                                    <div className="text-3xl font-bold text-green-700">{syncResult.created}</div>
                                    <div className="text-sm text-green-600">作成</div>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded p-4 text-center">
                                    <div className="text-3xl font-bold text-blue-700">{syncResult.updated}</div>
                                    <div className="text-sm text-blue-600">更新</div>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded p-4 text-center">
                                    <div className="text-3xl font-bold text-red-700">{syncResult.errors}</div>
                                    <div className="text-sm text-red-600">エラー</div>
                                </div>
                            </div>

                            {syncResult.errorDetails.length > 0 && (
                                <div className="mt-4">
                                    <h3 className="font-semibold text-gray-700 mb-2">エラー詳細:</h3>
                                    <div className="bg-red-50 border border-red-200 rounded p-3 max-h-60 overflow-y-auto">
                                        {syncResult.errorDetails.map((error, index) => (
                                            <div key={index} className="text-sm text-red-700 mb-1">
                                                {error}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t flex justify-end">
                            <button
                                onClick={() => setSyncResult(null)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
